<script>
  import { fade } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import { surveyStore, currentStep, elevatedExperienceApplies } from '../../stores/surveyStore';
  import ProgressBar from './ProgressBar.svelte';
  import EmailCapture from './EmailCapture.svelte';
  import Question from './Question.svelte';
  import ResultsPage from './ResultsPage.svelte';
  import { submitSurvey, saveIntermediate, fetchQuestions, generateTimestamp } from '../../services/api';
  import { onMount } from 'svelte';
  
  import unitedLogo from '../../assets/images/logos/united-logo.png';
  import foundationLogo from '../../assets/images/logos/foundation-logo.png';

  let isLoading = true;
  let loadError = null;
  let questions = [];

  export let embedded = false;
  export let embedOptions = {};

  onMount(async () => {
    try {
      isLoading = true;
      questions = await fetchQuestions();
      isLoading = false;

      const params = new URLSearchParams(window.location.search);
      let source = params.get('source') || params.get('utm_source');

      if (!source && document.referrer) {
        try {
          const refParams = new URLSearchParams(new URL(document.referrer).search);
          source = refParams.get('source') || refParams.get('utm_source');
        } catch (e) {
          console.warn('Failed to parse referrer:', document.referrer);
        }
      }

      // Stamp session sk — both reused for all saves this session
      const sk = generateTimestamp();
      surveyStore.update(store => ({
        ...store,
        sk,
        ...(source ? { source } : {})
      }));

      if (source) {
        console.log("Survey source set:", source);
      }
    } catch (error) {
      console.error('Failed to load questions:', error);
      loadError = 'Failed to load survey questions. Please try refreshing the page.';
      isLoading = false;
    }
  });
  
  let isTransitioning = false;

  // The elevated food & drink question (sk=3.5) only applies to Loudville /
  // Lower Bowl seating. It is otherwise skipped in both directions of the flow.
  function isElevatedQuestion(q) {
    return !!q && (q.id === 'multi-choice-3.5' || q.id === 'q3.5');
  }

  function shouldSkipStep(index) {
    const q = questions[index];
    if (!isElevatedQuestion(q)) return false;
    const seatingId = $surveyStore['seating-3'] || $surveyStore.seating ||
                      $surveyStore.q3 || $surveyStore['image-multi-choice-3'] || '';
    return !elevatedExperienceApplies(seatingId);
  }

  $: currentQuestion = questions[$currentStep];
  $: totalQuestions = questions.length;
  $: isLastQuestion = $currentStep === totalQuestions - 1;
  $: isFirstQuestion = $currentStep === 0;
  $: showResults = $currentStep === totalQuestions;
  
  function nextQuestion() {
    if (isTransitioning || isLastQuestion) return;
    
    isTransitioning = true;

    // Fire-and-forget intermediate save on every forward step
    saveIntermediate($surveyStore, currentQuestion, $currentStep);
    
    setTimeout(() => {
      currentStep.update(step => {
        let next = step + 1;
        while (next < totalQuestions && shouldSkipStep(next)) next++;
        return next;
      });
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }, 300);
  }
  
  function prevQuestion() {
    if (isTransitioning || isFirstQuestion) return;
    
    isTransitioning = true;
    
    setTimeout(() => {
      currentStep.update(step => {
        let prev = step - 1;
        while (prev > 0 && shouldSkipStep(prev)) prev--;
        return prev;
      });
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }, 300);
  }
  
  async function handleSubmit() {
    console.log('Survey store at submit time:', $surveyStore); 
    if (isTransitioning) return;
    
    isTransitioning = true;
    currentStep.set(questions.length);
    try {
      console.log('Submitting survey...', $surveyStore);
      await submitSurvey($surveyStore);
      currentStep.set(questions.length);
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }
  }

  let direction = 1;
  let prevStep = $currentStep;
  
  $: {
    if ($currentStep !== prevStep) {
      direction = $currentStep > prevStep ? 1 : -1;
      prevStep = $currentStep;
    }
  }

  function addPassiveTouchListeners() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('DOMContentLoaded', () => {
        const options = { passive: true };
        document.addEventListener('touchstart', () => {}, options);
        document.addEventListener('touchmove', () => {}, options);
      });
    }
  }

  addPassiveTouchListeners();

  const fadeOutDuration = 300;
  const fadeInDuration = 400; 
  const delayBetween = 200;
  
  function ensureRankingInitialized(question) {
    if (question && question.type === 'ranking') {
      if (!$surveyStore[question.id] || typeof $surveyStore[question.id] !== 'object') {
        const rankingValue = {};
        question.options.forEach((option, index) => {
          rankingValue[option.id] = index + 1;
        });
        surveyStore.update(store => ({ ...store, [question.id]: rankingValue }));
      }
    }
  }
  
  $: if (currentQuestion) {
    ensureRankingInitialized(currentQuestion);
  }
</script>

<div class="survey-container {embedded ? 'embedded' : ''}" style={embedded && embedOptions.maxWidth ? `max-width: ${embedOptions.maxWidth}` : ''}>
  {#if isLoading}
    <div class="loading-container card">
      <div class="loading-spinner"></div>
      <p>Loading survey questions...</p>
    </div>
  {:else if loadError}
    <div class="error-container card">
      <p>{loadError}</p>
      <button class="retry-button" on:click={() => window.location.reload()}>Try Again</button>
    </div>
  {:else if !showResults}
    <div class="progress-bar-wrapper">
      <ProgressBar currentStep={$currentStep} totalSteps={totalQuestions - 1} />
    </div>
    
    <div class="question-container mobile-friendly">
      {#key $currentStep}
        <div class="question-wrapper card" 
          in:fade={{ duration: fadeInDuration, delay: fadeOutDuration + delayBetween, easing: quintOut }}
          out:fade={{ duration: fadeOutDuration, easing: quintOut }}
        >
          {#if $currentStep === 1}
            <EmailCapture on:next={nextQuestion} />
          {:else if currentQuestion.type === 'intro'}
            <div class="intro-screen">
              <div class="logo-container">
                <img src={unitedLogo} alt="Cavs United Logo" class="united-logo" />
                <img src={foundationLogo} alt="Foundation Membership Logo" class="foundation-logo" />
              </div>
              <h1>{currentQuestion.title}</h1>
              {#if currentQuestion.mainParagraph}
                <p class="main-intro-paragraph">{currentQuestion.mainParagraph}</p>
              {/if}              
              <p class="sub-description">{currentQuestion.description}</p>
              <button class="cta-button" on:click={nextQuestion}>Let's get started</button>
            </div>
          {:else}
            <Question 
              question={currentQuestion} 
              on:answer={nextQuestion} 
              on:submit={handleSubmit} 
              isLast={isLastQuestion}
            />
          {/if}
        </div>
      {/key}
    </div>
    
    <div class="navigation">
      {#if !isFirstQuestion}
        <button class="nav-button prev" on:click={prevQuestion} disabled={isTransitioning}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
          </svg>
          Back
        </button>
      {:else}
        <div></div>
      {/if}
      
      {#if !isLastQuestion && $currentStep > 1}
        <button class="nav-button next" on:click={nextQuestion} disabled={isTransitioning}>
          Skip
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
          </svg>
        </button>
      {:else if $currentStep > 1}
        <div></div>
      {/if}
    </div>
  {:else}
    <ResultsPage />
  {/if}
</div>

<style>
  .survey-container {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: var(--spacing-md);
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: var(--background-color);
  }
  
  .embedded {
    max-width: 100%;
    padding: 0;
    border-radius: 0;
    background-color: transparent;
    box-shadow: none;
    overflow: visible;
  }
  .embedded .question-wrapper {
    background-color: transparent;
    box-shadow: none;
    border: none;
  }
  
  .embedded .progress-bar-wrapper {
    background-color: transparent;
  }
  
  .embedded .card {
    background-color: transparent;
    box-shadow: none;
    border: none;
  }
  
  .loading-container, .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    margin: var(--spacing-lg) auto;
    max-width: 500px;
  }
  
  .loading-container p, .error-container p {
    margin-top: var(--spacing-md);
    color: var(--text-color);
  }
  
  .retry-button {
    margin-top: var(--spacing-md);
  }
  
  .question-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    position: relative;
    margin-top: 0rem; 
  }

  .progress-bar-wrapper {
    width: 100%;
    margin: 0 auto var(--spacing-md) auto;
    padding: var(--spacing-xs) var(--spacing-md) 0;
    overflow: visible;
    z-index: 10;
    position: relative;
  }

  .question-wrapper {
    width: 100%;
    max-width: 800px;
    padding: var(--spacing-lg);
    position: relative;
  }
  
  .intro-screen {
    text-align: center;
    padding: var(--spacing-lg);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
  }
  
  .logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2rem;
    background: transparent;
  }

  /* The shield image includes the "Presented by KeyBank" lockup below the
     crest, so it renders taller than its visual width. Size both logos by
     height so the crest and the round Foundation mark read as a matched pair. */
  .united-logo {
    height: 150px;
    width: auto;
    max-width: 160px;
    margin-bottom: 0;
    background-color: transparent;
    object-fit: contain;
  }

  .foundation-logo {
    height: 110px;
    width: auto;
    max-width: 130px;
    margin-bottom: 0;
    background-color: transparent;
    object-fit: contain;
  }
  
  .intro-screen h1 {
    color: var(--cavs-wine);
    margin-bottom: 0rem;
    margin-top: 0rem;
  }
  
  .intro-screen p {
    margin-bottom: 0rem;
  }

  .intro-screen .main-intro-paragraph {
    margin-bottom: 0rem;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }

  .intro-screen .sub-description {
    margin-top: 1rem;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }
  
  .cta-button {
    background-color: #6A0032;
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
  }
  
  .navigation {
    display: flex;
    justify-content: space-between;
    margin-top: var(--spacing-lg);
    max-width: 800px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
  
  .nav-button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    color: var(--text-color);
    font-size: 0.9rem;
    cursor: pointer;
    padding: var(--spacing-xs) var(--spacing-md);
    transition: color 0.2s ease;
    box-shadow: none;
  }
  
  .nav-button:hover:not([disabled]) {
    color: var(--cavs-wine);
    background: none;
    transform: none;
  }
  
  .nav-button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    background: none;
  }
  
  .prev svg {
    margin-right: var(--spacing-xs);
    fill: currentColor;
  }
  
  .next svg {
    margin-left: var(--spacing-xs);
    fill: currentColor;
  }
  
  @media (max-width: 768px) {    
    .survey-container {
      padding: 10px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    
    .progress-bar-wrapper {
      padding: var(--spacing-md) var(--spacing-md) 0;
      width: 95%;
      margin: 0 auto var(--spacing-md) auto;
    }
    
    .question-wrapper {
      padding: var(--spacing-md) !important;
      margin: 0 !important;
      width: 100% !important;
      box-sizing: border-box;
    }

    .mobile-friendly {
      padding: 0 var(--spacing-md);
      margin: 0 auto var(--spacing-md) auto;
    }
    
    .option-item, .team-card, .rank-item {
      padding: 1rem;
    }
    
    .intro-screen {
      padding: var(--spacing-md);
      gap: 1rem;
    }
    
    .united-logo {
      height: 120px;
      max-width: 130px;
    }

    .foundation-logo {
      height: 88px;
      max-width: 105px;
    }
    
    .logo-container {
      padding: 0rem;
    }
    
    .navigation {
      margin-top: var(--spacing-md);
      padding: 0 var(--spacing-md);
    }
    
    .nav-button {
      font-size: 0.8rem;
      padding: var(--spacing-xs) var(--spacing-md);
    }
  }
</style>